import React from 'react'
import { Modal, Separator } from '@/shared/ui'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { listReportsByOpportunity } from '../services/auditoriaService'
import { useOportunidadeDetails } from '@/modules/oportunidades/hooks/useOportunidadeDetails'
import { buildContextParts } from '@/modules/oportunidades/utils/contextText'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'

export default function AuditReportsModal({ open, onOpenChange, item }: { open: boolean; onOpenChange: (v:boolean)=>void; item: OpportunityItem | null }) {
  const [reports, setReports] = React.useState<Array<{ id: string; text: string; email?: string | null; name?: string | null; created_at: string }>>([])
  const [loading, setLoading] = React.useState(false)
  const { data, asset } = useOportunidadeDetails(item as any)
  const ctx = React.useMemo(()=> buildContextParts(data), [data])
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  // Helpers iguais aos dos cards (timeframes, recommendation e objetivo)
  function tfShort(tfRaw?: string | null): string | null {
    const t = String(tfRaw || '').toUpperCase()
    if (!t) return null
    if (t === '5') return '5min'
    if (t === '15') return '15min'
    if (t === '30') return '30min'
    if (t === '60') return '1h'
    if (t === '240') return '4h'
    if (t === '720') return '12h'
    if (t === 'D') return '1D'
    if (t === 'W') return '1W'
    if (t === 'M') return '1M'
    return t
  }
  function minorForMajor(tfRaw?: string | null): string | null {
    const tf = String(tfRaw || '').toUpperCase()
    if (!tf) return null
    if (tf === '60') return '5'
    if (tf === '240') return '15'
    if (tf === '720') return '30'
    if (tf === 'D') return '60'
    if (tf === 'W') return '240'
    if (tf === 'M') return 'D'
    if (tf === '5' || tf === '15' || tf === '30') return tf
    return null
  }
  function toObjective(trend?: string | null): 'Long' | 'Short' | '' {
    const t = String(trend || '').toLowerCase()
    if (t === 'bullish' || t === 'alta' || t === 'long') return 'Long'
    if (t === 'bearish' || t === 'baixa' || t === 'short') return 'Short'
    return ''
  }
  function toRecommendation(row: any): string | null {
    const tf = String(row?.target_timeframe || '').toUpperCase()
    if (tf === '60') return 'Scalp Trade'
    if (tf === '240') return 'Scalp / Day Trade'
    if (tf === '720') return 'Day Trade'
    if (tf === 'D') return 'Day / Swing Trade'
    if (tf === 'W') return 'Swing Trade'
    if (tf === 'M') return 'Swing / Position Trade'
    const tf2 = String(row?.timeframe || '').toUpperCase()
    if (tf2 === '60') return 'Scalp Trade'
    if (tf2 === '240') return 'Scalp / Day Trade'
    if (tf2 === '720') return 'Day Trade'
    if (tf2 === 'D') return 'Day / Swing Trade'
    if (tf2 === 'W') return 'Swing Trade'
    if (tf2 === 'M') return 'Swing / Position Trade'
    if (tf2 === '5' || tf2 === '15' || tf2 === '30') return 'Scalp Trade'
    return null
  }
  const tfLower = tfShort(minorForMajor((data as any)?.target_timeframe) || (data as any)?.timeframe)
  const tfUpper = tfShort((data as any)?.target_timeframe)
  const recommendation = toRecommendation(data)
  const objective = toObjective(String((data as any)?.target_trend || (data as any)?.trend))

  React.useEffect(()=>{
    if (!open || !item?.id) return
    let alive = true
    setLoading(true)
    listReportsByOpportunity(item.id).then((rows)=>{
      if (!alive) return
      setReports(rows.map(r=>({ id: r.id, text: r.report_text, email: r.reported_email, name: r.reported_name, created_at: r.created_at })))
    }).finally(()=> setLoading(false))
    return ()=> { alive = false }
  }, [open, item?.id])

  const [openId, setOpenId] = React.useState<string | null>(null)
  React.useEffect(()=>{ if(!open) setOpenId(null) }, [open])
  return (
    <Modal open={open} onOpenChange={onOpenChange} className="max-w-[960px]">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          {asset?.logo_url ? (
            <img src={asset.logo_url} alt={item?.symbol || ''} className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-muted" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold">{asset?.name || item?.name || item?.symbol}</div>
            <div className="text-xs text-foreground/70">{item?.symbol} - {item?.objective || '-'} - {item?.fractalUpper || '-'}</div>
          </div>
          <div className="ml-2 hidden flex-1 items-center justify-end gap-6 text-xs text-foreground/80 md:flex">
            <span>{tfLower || '-'} {tfLower || tfUpper ? '|' : ''} {tfUpper || '-'}</span>
            <span>{recommendation || '-'}</span>
            <span className={[objective==='Long'?'text-emerald-400':objective==='Short'?'text-red-400':'text-foreground/80','font-medium'].join(' ')}>{objective || '-'}</span>
          </div>
        </div>
        <Separator />
        {ctx?.header && <div className="text-sm font-medium">{ctx.header}</div>}
        {ctx?.emasLine && <div className="text-xs text-foreground/70">{ctx.emasLine}</div>}
        {ctx?.fiboLine && <div className="text-sm text-foreground/80">{ctx.fiboLine}</div>}
        {ctx?.ema200Line && <div className="text-sm text-foreground/80">{ctx.ema200Line}</div>}
        <Separator />
        {loading ? (
          <div className="text-sm text-foreground/70">Carregando relatos...</div>
        ) : reports.length === 0 ? (
          <div className="text-sm text-foreground/70">Nenhum relato encontrado.</div>
        ) : (
          <div className="max-h-[60vh] space-y-2 overflow-auto pr-1">
            {reports.map((r)=> {
              const active = openId === r.id
              return (
                <div key={r.id} className={["rounded-md border bg-background/50", active ? 'border-primary' : 'border-border/60'].join(' ')}>
                  <button type="button" onClick={()=> setOpenId(r.id)} className="flex w-full items-center justify-between px-3 py-2 text-left">
                    <span className="text-xs text-foreground/70">{isMaster ? ((r.name || 'Usuário') + (r.email ? ` (${r.email})` : '')) : 'Usuário'}</span>
                    <span className="text-xs text-foreground/70">{formatDateTime(r.created_at)}</span>
                  </button>
                  {active && (
                    <div className="border-t border-border/60 px-3 py-2">
                      <div className="whitespace-pre-wrap text-sm">{r.text}</div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}

function formatDateTime(iso?: string) {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    return d.toLocaleString()
  } catch { return iso }
}
